import { AfterViewInit, Component, ElementRef, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { NavigationService, NavItem } from '../../../core/services/navigation.service';

@Component({
    selector: 'app-feature-tabs',
    standalone: true,
    imports: [CommonModule, RouterModule],
    templateUrl: './feature-tabs.component.html',
    styleUrl: './feature-tabs.component.scss'
})
export class FeatureTabsComponent implements AfterViewInit, OnDestroy {
    navService = inject(NavigationService);
    router = inject(Router);
    private host = inject(ElementRef<HTMLElement>);
    private resize?: ResizeObserver;

    /**
     * Publish the strip's REAL rendered height as --zf-tabs-h so sticky page
     * headers (top: --zf-sticky-top) sit flush under it. It used to be derived
     * from the nav config on NavigationEnd, which went stale whenever the tab
     * list changed without a navigation (permissions / module flags loading
     * late) — headers then stuck 44px low with content showing through.
     */
    ngAfterViewInit(): void {
        const publish = () =>
            document.documentElement.style.setProperty(
                '--zf-tabs-h',
                `${Math.round(this.host.nativeElement.getBoundingClientRect().height)}px`,
            );
        this.resize = new ResizeObserver(publish);
        this.resize.observe(this.host.nativeElement);
        publish();
    }

    ngOnDestroy(): void {
        this.resize?.disconnect();
        document.documentElement.style.setProperty('--zf-tabs-h', '0px');
    }

    get tabs(): NavItem[] {
        return this.navService.getTabsForActiveCategory();
    }

    get showTabs(): boolean {
        return this.tabs.length > 0;
    }

    isActive(item: NavItem): boolean {
        const currentUrl = this.router.url.split('?')[0];
        const fullUrl = this.router.url;

        if (item.queryParams?.tab) {
            return fullUrl.includes(`tab=${item.queryParams.tab}`);
        }

        // If it's the base route and there are no other specific matches with query params
        if (item.route === currentUrl && !fullUrl.includes('tab=')) return true;

        // Also check subroutes
        return item.subRoutes?.some(sr => currentUrl.startsWith(sr)) || false;
    }
}
